class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.selectedProducts = new Map();
    this.maxQuantityPerProduct = 999; // Effectively unlimited
    this.milestones = [
      { quantity: 3, discount: 0.60, price: 2991 },   // 3 for £29.91 (60% off)
      { quantity: 4, discount: 0.615, price: 3828 },  // 4 for £38.28 (61.5% off)
      { quantity: 5, discount: 0.63, price: 4620 }    // 5 for £46.20 (63% off)
    ];
    this.regularPrice = 2485; // £24.85 in pence
  }

  connectedCallback() {
    this.progressBar = this.querySelector('[data-progress-fill]');
    this.progressText = this.querySelector('[data-progress-text]');
    this.productCards = this.querySelectorAll('.bundle-product-card');
    this.sidebarItems = this.querySelector('[data-sidebar-items]');
    this.sidebarPricing = this.querySelector('[data-sidebar-pricing]');
    this.addBundleButton = this.querySelector('[data-add-bundle-button]');
    this.buttonText = this.querySelector('[data-button-text]');
    this.floatingCart = this.querySelector('[data-floating-cart]');
    this.floatingCount = this.querySelector('[data-floating-count]');
    this.floatingPrice = this.querySelector('[data-floating-price]');
    this.floatingAddButton = this.querySelector('[data-floating-add-button]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.searchClear = this.querySelector('[data-search-clear]');
    this.productsContainer = this.querySelector('[data-bundle-products]');
    
    // Modal elements
    this.modal = this.querySelector('[data-description-modal]');
    this.modalClose = this.querySelector('[data-modal-close]');
    this.modalImage = this.querySelector('[data-modal-image]');
    this.modalTitle = this.querySelector('[data-modal-title]');
    this.modalPrice = this.querySelector('[data-modal-price]');
    this.modalDescription = this.querySelector('[data-modal-description]');
    this.modalNotes = this.querySelector('[data-modal-notes]');
    this.modalAddBtn = this.querySelector('[data-modal-add-btn]');
    this.currentModalVariantId = null;
    this.currentModalCard = null;
    
    // Drawer elements
    this.drawer = this.querySelector('[data-bundle-drawer]');
    this.drawerOverlay = this.querySelector('[data-drawer-overlay]');
    this.drawerToggleBtn = this.querySelector('[data-drawer-toggle]');
    this.drawerCloseBtn = this.querySelector('[data-drawer-close]');
    this.drawerItemsContainer = this.querySelector('[data-drawer-items]');
    this.drawerQuantityEl = this.querySelector('[data-drawer-count]');
    this.drawerProgressBar = this.querySelector('[data-drawer-progress]');
    this.drawerProgressText = this.querySelector('[data-drawer-progress-text]');
    this.drawerAddToCartBtn = this.querySelector('[data-drawer-add-button]');

    // Sort products: available first, out of stock last
    this.sortProductsByAvailability();

    this.initializeEventListeners();
    this.loadSavedBundle(); // This will call updateUI() if there's saved data
    
    // Only call updateUI if there's no saved bundle
    if (!localStorage.getItem('scentReserveBundle')) {
      this.updateUI();
    }
  }

  sortProductsByAvailability() {
    if (!this.productsContainer) return;
    
    // Convert NodeList to Array and sort
    const cardsArray = Array.from(this.productCards);
    
    cardsArray.sort((a, b) => {
      const aAvailable = a.dataset.productAvailable === 'true';
      const bAvailable = b.dataset.productAvailable === 'true';
      
      // Available products come first
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
      return 0; // Keep original order for products with same availability
    });
    
    // Re-append cards in sorted order
    cardsArray.forEach(card => {
      this.productsContainer.appendChild(card);
    });
    
    // Update the productCards NodeList after sorting
    this.productCards = this.querySelectorAll('.bundle-product-card');
  }

  initializeEventListeners() {
    // Product card clicks
    this.productCards.forEach(card => {
      // Add button click
      const addButton = card.querySelector('[data-add-button]');
      if (addButton) {
        addButton.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleProduct(card);
        });
      }

      // Remove the general card click handler to prevent accidental toggling
      // Users should use the specific buttons (add/remove, quantity controls) instead

      // Quantity controls
      const decreaseBtn = card.querySelector('[data-quantity-decrease]');
      const increaseBtn = card.querySelector('[data-quantity-increase]');

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.updateQuantity(card, -1);
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.updateQuantity(card, 1);
        });
      }

      // Handle product link clicks to show modal
      const productLink = card.querySelector('[data-product-link]');
      if (productLink) {
        productLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.showProductModal(productLink, card);
        });
      }
    });

    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.searchProducts(e.target.value);
      });

      this.searchInput.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
          this.clearSearch();
        }
      });
    }

    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => {
        this.clearSearch();
      });
    }

    // Gender filter buttons
    const filterButtons = this.querySelectorAll('[data-filter-value]');
    filterButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.currentGenderFilter = btn.dataset.filterValue;
        this.applyFilters();
        
        // Update active state
        filterButtons.forEach(b => b.classList.remove('bundle-filter-btn--active'));
        btn.classList.add('bundle-filter-btn--active');
      });
    });

    // Initialize search/filter state
    this.currentGenderFilter = 'all';
    this.currentSearch = '';

    // Add to cart buttons
    this.addBundleButton.addEventListener('click', () => this.addBundleToCart());
    this.floatingAddButton.addEventListener('click', () => this.addBundleToCart());
    
    // Modal event listeners
    if (this.modalClose) {
      this.modalClose.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.closeModal();
      });
    }
    
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });
    }
    
    if (this.modalAddBtn) {
      this.modalAddBtn.addEventListener('click', () => {
        if (this.currentModalCard) {
          this.toggleProduct(this.currentModalCard);
          this.closeModal();
        }
      });
    }
    
    // Drawer event listeners
    if (this.drawerToggleBtn) {
      this.drawerToggleBtn.addEventListener('click', () => this.toggleDrawer());
    }
    
    if (this.drawerCloseBtn) {
      this.drawerCloseBtn.addEventListener('click', () => this.closeDrawer());
    }
    
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
    }
    
    if (this.drawerAddToCartBtn) {
      this.drawerAddToCartBtn.addEventListener('click', () => {
        this.addBundleToCart();
        this.closeDrawer();
      });
    }
    
    // Add swipe gestures for drawer
    if (this.drawer) {
      let startY = 0;
      let currentY = 0;
      let isDragging = false;
      
      const drawerContent = this.drawer.querySelector('.bundle-drawer__content');
      
      drawerContent.addEventListener('touchstart', (e) => {
        startY = e.touches[0].clientY;
        isDragging = true;
      });
      
      drawerContent.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        const diff = currentY - startY;
        
        // Only allow downward swipe
        if (diff > 0) {
          drawerContent.style.transform = `translateY(${diff}px)`;
        }
      });
      
      drawerContent.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        
        const diff = currentY - startY;
        drawerContent.style.transform = '';
        
        // Close if swiped down more than 100px
        if (diff > 100) {
          this.closeDrawer();
        }
      });
    }

    // Save bundle on page unload
    window.addEventListener('beforeunload', () => this.saveBundleToStorage());
  }

  searchProducts(searchTerm) {
    this.currentSearch = searchTerm.toLowerCase().trim();
    this.applyFilters();
  }

  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
      this.currentSearch = '';
      this.applyFilters();
    }
  }

  applyFilters() {
    let visibleCount = 0;
    const visibleCards = [];
    const hiddenCards = [];
    
    this.productCards.forEach(card => {
      const title = (card.dataset.productTitle || '').toLowerCase();
      const tags = (card.dataset.productTags || '').toLowerCase();
      const gender = (card.dataset.productGender || '').toLowerCase();
      
      // Check search match
      const searchMatch = !this.currentSearch || 
                         title.includes(this.currentSearch) || 
                         tags.includes(this.currentSearch);
      
      // Check gender filter match
      const genderMatch = this.currentGenderFilter === 'all' || 
                         gender === this.currentGenderFilter;
      
      // Show/hide based on both conditions
      if (searchMatch && genderMatch) {
        card.classList.remove('bundle-product-card--hidden');
        visibleCards.push(card);
        visibleCount++;
      } else {
        card.classList.add('bundle-product-card--hidden');
        hiddenCards.push(card);
      }
    });
    
    // Sort visible cards by availability (available first, out-of-stock last)
    if (visibleCards.length > 0) {
      visibleCards.sort((a, b) => {
        const aAvailable = a.dataset.productAvailable === 'true';
        const bAvailable = b.dataset.productAvailable === 'true';
        
        if (aAvailable && !bAvailable) return -1;
        if (!aAvailable && bAvailable) return 1;
        return 0;
      });
      
      // Re-append all cards in the correct order
      visibleCards.forEach(card => {
        this.productsContainer.appendChild(card);
      });
      hiddenCards.forEach(card => {
        this.productsContainer.appendChild(card);
      });
    }

    // Show/hide no results message
    this.updateNoResultsMessage(visibleCount);
  }

  updateNoResultsMessage(visibleCount) {
    let noResultsEl = this.querySelector('.bundle-no-results');
    
    if (visibleCount === 0) {
      if (!noResultsEl) {
        noResultsEl = document.createElement('div');
        noResultsEl.className = 'bundle-no-results';
        noResultsEl.innerHTML = `
          <h3>No products found</h3>
          <p>Try adjusting your search or filters</p>
        `;
        this.productsContainer.appendChild(noResultsEl);
      }
    } else if (noResultsEl) {
      noResultsEl.remove();
    }
  }


  toggleProduct(card) {
    const productId = card.dataset.productId;
    const isAvailable = card.dataset.productAvailable === 'true';
    
    // Don't allow toggling out-of-stock products
    if (!isAvailable) {
      return;
    }
    
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
      this.updateProductCard(card, 0);
    } else {
      // Get the actual product image, not any overlay badges
      const productImage = card.querySelector('.bundle-product-card__image');
      const imageUrl = productImage ? productImage.src : null;
      
      this.selectedProducts.set(productId, {
        id: productId,
        variantId: card.dataset.variantId,
        title: card.dataset.productTitle,
        price: parseInt(card.dataset.productPrice),
        handle: card.dataset.productHandle,
        quantity: 1,
        image: imageUrl
      });
      this.updateProductCard(card, 1);
    }
    
    this.updateUI();
    this.saveBundleToStorage();
  }

  updateQuantity(card, delta) {
    const productId = card.dataset.productId;
    const product = this.selectedProducts.get(productId);
    
    if (!product) return;
    
    const newQuantity = Math.max(0, product.quantity + delta);
    
    if (newQuantity === 0) {
      this.selectedProducts.delete(productId);
      this.updateProductCard(card, 0);
    } else {
      product.quantity = newQuantity;
      this.updateProductCard(card, newQuantity);
    }
    
    this.updateUI();
    this.saveBundleToStorage();
  }

  updateProductCard(card, quantity) {
    const badge = card.querySelector('[data-quantity-badge]');
    const quantitySelector = card.querySelector('[data-quantity-selector]');
    const quantityValue = card.querySelector('[data-quantity-value]');
    const decreaseBtn = card.querySelector('[data-quantity-decrease]');
    const addButton = card.querySelector('[data-add-button]');
    const addText = card.querySelector('[data-add-text]');
    
    if (quantity > 0) {
      card.classList.add('bundle-product-card--selected');
      badge.style.display = 'flex';
      badge.textContent = quantity;
      
      if (quantitySelector) {
        quantitySelector.style.display = 'flex';
        quantityValue.textContent = quantity;
      }
      
      if (addButton) {
        addButton.style.display = 'none';
      }
      
      // Never disable the decrease button - it should always work
      if (decreaseBtn) {
        decreaseBtn.disabled = false;
      }
    } else {
      card.classList.remove('bundle-product-card--selected');
      badge.style.display = 'none';
      
      if (quantitySelector) {
        quantitySelector.style.display = 'none';
      }
      
      if (addButton) {
        addButton.style.display = '';
      }
      
      if (addText) {
        addText.textContent = 'Add to Bundle';
      }
    }
  }

  updateUI() {
    const totalQuantity = this.getTotalQuantity();
    const pricing = this.calculatePricing(totalQuantity);
    
    // Update progress bar
    this.updateProgressBar(totalQuantity);
    
    // Update milestones
    this.updateMilestones(totalQuantity);
    
    // Update sidebar
    this.updateSidebar(pricing);
    
    // Update floating cart
    this.updateFloatingCart(totalQuantity, pricing);
    
    // Update buttons
    this.updateButtons(totalQuantity);
    
    // Update product card prices
    this.updateProductPrices(totalQuantity);
    
    // Update drawer toggle visibility
    if (this.drawerToggleBtn) {
      this.drawerToggleBtn.style.display = totalQuantity > 0 ? 'flex' : 'none';
    }
  }

  updateProgressBar(quantity) {
    const maxQuantity = 5;
    const percentage = Math.min(100, (quantity / maxQuantity) * 100);
    
    this.progressBar.style.width = `${percentage}%`;
    
    if (quantity < 3) {
      this.progressText.textContent = `${quantity}/3`;
    } else if (quantity < 4) {
      this.progressText.textContent = `${quantity}/4`;
    } else if (quantity < 5) {
      this.progressText.textContent = `${quantity}/5`;
    } else {
      this.progressText.textContent = `${quantity} 🎉`;
    }

    // Add celebration animation at milestones
    if (quantity === 3 || quantity === 4 || quantity === 5) {
      this.progressBar.style.animation = 'pulse 0.5s ease';
      setTimeout(() => {
        this.progressBar.style.animation = '';
      }, 500);
    }
  }

  updateMilestones(quantity) {
    const milestones = this.querySelectorAll('[data-milestone]');
    milestones.forEach(milestone => {
      const target = parseInt(milestone.dataset.milestone);
      if (quantity >= target) {
        milestone.classList.add('bundle-progress__milestone--active');
      } else {
        milestone.classList.remove('bundle-progress__milestone--active');
      }
    });
  }

  updateSidebar(pricing) {
    const totalQuantity = this.getTotalQuantity();
    
    // Update items list
    if (this.selectedProducts.size === 0) {
      this.sidebarItems.innerHTML = '<p class="text-subdued text-center py-4">Select 3 or more bottles to create your bundle</p>';
      this.sidebarPricing.style.display = 'none';
    } else {
      let itemsHTML = '';
      this.selectedProducts.forEach(product => {
        itemsHTML += `
          <div class="bundle-sidebar__item" data-sidebar-product-id="${product.id}">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" class="bundle-sidebar__item-image">` : ''}
            <div class="bundle-sidebar__item-details">
              <div class="bundle-sidebar__item-title">${product.title}</div>
              <div class="bundle-sidebar__item-quantity">Qty: ${product.quantity}</div>
            </div>
            <button class="bundle-sidebar__item-remove" data-remove-product="${product.id}" aria-label="Remove ${product.title}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        `;
      });
      this.sidebarItems.innerHTML = itemsHTML;
      
      // Add event listeners to remove buttons
      this.sidebarItems.querySelectorAll('[data-remove-product]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const productId = e.currentTarget.dataset.removeProduct;
          const card = this.querySelector(`[data-product-id="${productId}"]`);
          if (card) {
            this.selectedProducts.delete(productId);
            this.updateProductCard(card, 0);
            this.updateUI();
            this.saveBundleToStorage();
          }
        });
      });
      
      // Only show pricing when 3 or more items are selected
      if (totalQuantity >= 3) {
        this.sidebarPricing.style.display = 'block';
        
        // Only show discount row if there's actually a discount
        const discountRow = this.querySelector('.bundle-sidebar__discount-row');
        if (pricing.discountAmount > 0) {
        if (!discountRow) {
          // Create discount row if it doesn't exist
          const priceRowsContainer = this.sidebarPricing.querySelector('.bundle-sidebar__price-rows');
          if (!priceRowsContainer) {
            // Restructure pricing HTML
            this.sidebarPricing.innerHTML = `
              <div class="bundle-sidebar__price-rows">
                <div class="bundle-sidebar__price-row">
                  <span>Subtotal:</span>
                  <span data-original-price>£0.00</span>
                </div>
                <div class="bundle-sidebar__price-row bundle-sidebar__discount-row bundle-sidebar__savings">
                  <span>Discount:</span>
                  <span data-discount-amount>-£0.00</span>
                </div>
                <div class="bundle-sidebar__price-row bundle-sidebar__price-row--total">
                  <span>Total:</span>
                  <span data-total-price>£0.00</span>
                </div>
              </div>
            `;
          }
        }
        if (this.querySelector('.bundle-sidebar__discount-row')) {
          this.querySelector('.bundle-sidebar__discount-row').style.display = 'flex';
        }
        this.querySelector('[data-discount-amount]').textContent = `-${this.formatMoney(pricing.discountAmount)}`;
      } else {
        // Hide discount row if no discount
        if (discountRow) {
          discountRow.style.display = 'none';
        }
      }
      
        this.querySelector('[data-original-price]').textContent = this.formatMoney(pricing.originalPrice);
        this.querySelector('[data-total-price]').textContent = this.formatMoney(pricing.finalPrice);
      } else {
        this.sidebarPricing.style.display = 'none';
        // Show a hint about needing more items
        if (totalQuantity > 0) {
          const remaining = 3 - totalQuantity;
          this.sidebarItems.innerHTML += `
            <div class="text-subdued text-center py-3 border-t mt-3">
              Add ${remaining} more bottle${remaining !== 1 ? 's' : ''} to unlock bundle pricing
            </div>
          `;
        }
      }
    }
  }

  updateFloatingCart(quantity, pricing) {
    if (quantity === 0) {
      this.floatingCount.textContent = 'No items selected';
      this.floatingPrice.textContent = '';
    } else {
      this.floatingCount.textContent = `${quantity} item${quantity !== 1 ? 's' : ''} selected`;
      // Only show price when a deal is active (3+ items)
      if (quantity >= 3) {
        this.floatingPrice.textContent = this.formatMoney(pricing.finalPrice);
      } else {
        this.floatingPrice.textContent = '';
      }
    }
  }

  updateButtons(quantity) {
    if (quantity === 0) {
      this.buttonText.textContent = 'Select items to continue';
      this.addBundleButton.disabled = true;
      this.floatingAddButton.disabled = true;
      this.floatingAddButton.textContent = 'Add to Cart';
    } else if (quantity < 3) {
      const remaining = 3 - quantity;
      this.buttonText.textContent = `Add ${remaining} more for 60% off`;
      this.addBundleButton.disabled = true;
      this.floatingAddButton.disabled = true;
      this.floatingAddButton.textContent = `${remaining} more for deal`;
    } else {
      this.buttonText.textContent = 'Add Bundle to Cart';
      this.addBundleButton.disabled = false;
      this.floatingAddButton.disabled = false;
      this.floatingAddButton.textContent = 'Add to Cart';
    }
  }

  getTotalQuantity() {
    let total = 0;
    this.selectedProducts.forEach(product => {
      total += product.quantity;
    });
    return total;
  }
  
  getNextMilestone() {
    const totalQuantity = this.getTotalQuantity();
    for (const milestone of this.milestones) {
      if (totalQuantity < milestone.quantity) {
        return milestone;
      }
    }
    return null;
  }

  calculatePricing(quantity) {
    const originalPrice = quantity * this.regularPrice;
    let finalPrice = originalPrice;
    let discountAmount = 0;
    
    // Apply discount based on milestones
    if (quantity >= 5) {
      finalPrice = 4620; // £46.20 for 5 items
      discountAmount = originalPrice - finalPrice;
    } else if (quantity >= 4) {
      finalPrice = 3828; // £38.28 for 4 items
      discountAmount = originalPrice - finalPrice;
    } else if (quantity >= 3) {
      finalPrice = 2991; // £29.91 for 3 items
      discountAmount = originalPrice - finalPrice;
    }
    
    return {
      originalPrice,
      finalPrice,
      discountAmount,
      discountPercentage: discountAmount > 0 ? (discountAmount / originalPrice * 100).toFixed(0) : 0
    };
  }

  formatMoney(cents) {
    return `£${(cents / 100).toFixed(2)}`;
  }

  async addBundleToCart() {
    const items = [];
    this.selectedProducts.forEach(product => {
      items.push({
        id: product.variantId,
        quantity: product.quantity
      });
    });

    if (items.length === 0) return;

    // Disable buttons and show loading state
    this.addBundleButton.disabled = true;
    this.floatingAddButton.disabled = true;
    const originalButtonText = this.buttonText.textContent;
    this.buttonText.textContent = 'Adding to cart...';
    this.floatingAddButton.textContent = 'Adding...';

    try {
      // Prepare sections to bundle (matching Impact theme pattern)
      let sectionsToBundle = ['cart-drawer', 'cart-count'];
      
      // Dispatch event to prepare bundled sections
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { 
        bubbles: true, 
        detail: { sections: sectionsToBundle } 
      }));
      
      // Create form data for each item and add them sequentially
      // This ensures compatibility with the cart drawer
      for (const item of items) {
        const formData = new FormData();
        formData.append('id', item.id);
        formData.append('quantity', item.quantity);
        formData.append('sections', sectionsToBundle.join(','));
        formData.append('sections_url', `${window.Shopify.routes.root}variants/${item.id}`);
        
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to add items to cart');
        }

        const responseJson = await response.json();
        
        // For the last item, handle the cart update
        if (items.indexOf(item) === items.length - 1) {
          // Get updated cart
          const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
          cartContent['sections'] = responseJson['sections'];
          
          // Clear the bundle
          this.clearBundle();
          
          // Dispatch events matching the theme's pattern
          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
              cart: cartContent
            }
          }));
          
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          }));
        }
      }

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.buttonText.textContent = 'Error - Try Again';
      this.floatingAddButton.textContent = 'Error';
      
      setTimeout(() => {
        this.buttonText.textContent = originalButtonText;
        this.floatingAddButton.textContent = 'Add to Cart';
        this.addBundleButton.disabled = false;
        this.floatingAddButton.disabled = false;
      }, 2000);
    }
  }

  clearBundle() {
    this.selectedProducts.clear();
    this.productCards.forEach(card => {
      this.updateProductCard(card, 0);
    });
    this.updateUI();
    localStorage.removeItem('snapBarBundle');
  }

  showSuccessMessage() {
    const message = document.createElement('div');
    message.className = 'bundle-success-message';
    message.innerHTML = `
      <div class="bundle-success-message__content">
        <span>🎉 Bundle added to cart!</span>
        <a href="/cart" class="button button--sm">View Cart</a>
      </div>
    `;
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--success-background);
      color: white;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--rounded);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 100;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }

  saveBundleToStorage() {
    const bundleData = [];
    this.selectedProducts.forEach(product => {
      bundleData.push({
        id: product.id,
        quantity: product.quantity
      });
    });
    localStorage.setItem('scentReserveBundle', JSON.stringify(bundleData));
  }

  loadSavedBundle() {
    const savedBundle = localStorage.getItem('scentReserveBundle');
    if (!savedBundle) return;

    try {
      const bundleData = JSON.parse(savedBundle);
      bundleData.forEach(item => {
        const card = this.querySelector(`[data-product-id="${item.id}"]`);
        if (card) {
          // Get the actual product image, not any overlay badges
          const productImage = card.querySelector('.bundle-product-card__image');
          const imageUrl = productImage ? productImage.src : null;
          
          this.selectedProducts.set(item.id, {
            id: item.id,
            variantId: card.dataset.variantId,
            title: card.dataset.productTitle,
            price: parseInt(card.dataset.productPrice),
            handle: card.dataset.productHandle,
            quantity: item.quantity,
            image: imageUrl
          });
          this.updateProductCard(card, item.quantity);
        }
      });
      
      // Update all UI elements after loading saved bundle
      this.updateUI();
    } catch (error) {
      console.error('Error loading saved bundle:', error);
      localStorage.removeItem('snapBarBundle');
    }
  }
  
  showProductModal(linkElement, card) {
    if (!this.modal) return;
    
    // Get product data from the link element
    const title = linkElement.dataset.productTitle || '';
    const price = linkElement.dataset.productPrice || '';
    const image = linkElement.dataset.productImage || '';
    const variantId = linkElement.dataset.variantId || card.dataset.productId;
    const productIndex = linkElement.dataset.productIndex;
    
    // Get the description from the template element
    const descriptionTemplate = card.querySelector(`[data-product-description="${productIndex}"]`);
    let description = '';
    if (descriptionTemplate) {
      description = descriptionTemplate.innerHTML.trim();
    }
    
    // Get the notes from the template element
    const notesTemplate = card.querySelector(`[data-product-notes="${productIndex}"]`);
    let notes = '';
    if (notesTemplate) {
      notes = notesTemplate.innerHTML.trim();
    }
    
    // Update modal content
    if (this.modalTitle) this.modalTitle.textContent = title;
    if (this.modalNotes) {
      // Set the notes HTML content
      this.modalNotes.innerHTML = notes;
    }
    if (this.modalDescription) {
      // Set the HTML content directly - it's already proper HTML from the template
      this.modalDescription.innerHTML = description;
    }
    if (this.modalPrice) this.modalPrice.textContent = price;
    if (this.modalImage) {
      this.modalImage.src = image;
      this.modalImage.alt = title;
    }
    
    // Update add button text based on selection state and availability
    if (this.modalAddBtn) {
      const isSelected = this.selectedProducts.has(card.dataset.productId);
      const isAvailable = card.dataset.productAvailable === 'true';
      
      if (!isAvailable) {
        this.modalAddBtn.textContent = 'Out of Stock';
        this.modalAddBtn.disabled = true;
        this.modalAddBtn.classList.add('bundle-description-modal__add-btn--unavailable');
      } else {
        this.modalAddBtn.textContent = isSelected ? 'Remove from Bundle' : 'Add to Bundle';
        this.modalAddBtn.disabled = false;
        this.modalAddBtn.classList.remove('bundle-description-modal__add-btn--unavailable');
      }
    }
    
    // Store current card reference for add button
    this.currentModalCard = card;
    this.currentModalVariantId = variantId;
    
    // Show modal
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  
  closeModal() {
    if (this.modal) {
      this.modal.classList.remove('active');
      document.body.style.overflow = '';
      this.currentModalVariantId = null;
      this.currentModalCard = null;
    }
  }
  
  toggleDrawer() {
    if (this.drawer && this.drawer.classList.contains('active')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }
  
  openDrawer() {
    if (!this.drawer) return;
    
    // Update drawer content
    this.updateDrawerContent();
    
    // Show drawer and overlay
    this.drawer.classList.add('active');
    if (this.drawerOverlay) {
      this.drawerOverlay.classList.add('active');
    }
    document.body.style.overflow = 'hidden';
  }
  
  closeDrawer() {
    if (!this.drawer) return;
    
    this.drawer.classList.remove('active');
    if (this.drawerOverlay) {
      this.drawerOverlay.classList.remove('active');
    }
    document.body.style.overflow = '';
  }
  
  updateDrawerContent() {
    if (!this.drawerItemsContainer) return;
    
    // Clear existing items
    this.drawerItemsContainer.innerHTML = '';
    
    // Add each selected product
    this.selectedProducts.forEach(product => {
      const itemEl = document.createElement('div');
      itemEl.className = 'bundle-drawer__item';
      itemEl.innerHTML = `
        <img src="${product.image}" alt="${product.title}" class="bundle-drawer__item-image">
        <div class="bundle-drawer__item-details">
          <div class="bundle-drawer__item-title">${product.title}</div>
          <div class="bundle-drawer__item-quantity">Qty: ${product.quantity}</div>
        </div>
        <button type="button" class="bundle-drawer__item-remove" data-remove-product="${product.id}">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      
      // Add remove event listener
      const removeBtn = itemEl.querySelector('[data-remove-product]');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          const card = this.querySelector(`[data-product-id="${product.id}"]`);
          if (card) {
            this.toggleProduct(card);
            this.updateDrawerContent();
          }
        });
      }
      
      this.drawerItemsContainer.appendChild(itemEl);
    });
    
    // Update drawer progress and pricing
    const totalQuantity = this.getTotalQuantity();
    const pricing = this.calculatePricing(totalQuantity);
    const nextMilestone = this.getNextMilestone();
    
    // Update quantity in header
    if (this.drawerQuantityEl) {
      this.drawerQuantityEl.textContent = totalQuantity;
    }
    
    // Update pricing
    const subtotalEl = this.querySelector('[data-drawer-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = this.formatMoney(pricing.finalPrice);
    }
    
    const savingsEl = this.querySelector('[data-drawer-savings]');
    const savingsAmountEl = this.querySelector('[data-drawer-savings-amount]');
    if (savingsEl && savingsAmountEl) {
      if (pricing.discountAmount > 0) {
        savingsEl.style.display = 'flex';
        savingsAmountEl.textContent = this.formatMoney(pricing.discountAmount);
      } else {
        savingsEl.style.display = 'none';
      }
    }
    
    // Update progress text
    const progressTextEl = this.querySelector('[data-drawer-progress-text]');
    if (progressTextEl) {
      if (totalQuantity === 0) {
        progressTextEl.textContent = 'Select items to start building your bundle';
      } else if (nextMilestone && totalQuantity < nextMilestone.quantity) {
        const remaining = nextMilestone.quantity - totalQuantity;
        progressTextEl.textContent = `${remaining} more for ${nextMilestone.quantity} for £${(nextMilestone.price / 100).toFixed(2)} deal`;
      } else if (totalQuantity >= 5) {
        progressTextEl.textContent = '🎉 Best deal unlocked! 5 for £46.20';
      } else if (totalQuantity >= 4) {
        progressTextEl.textContent = '🎉 Deal unlocked! 4 for £38.28';
      } else if (totalQuantity >= 3) {
        progressTextEl.textContent = '🎉 Deal unlocked! 3 for £29.91';
      }
    }
    
    // Update add to cart button
    const addToCartBtn = this.querySelector('[data-drawer-add-button]');
    if (addToCartBtn) {
      if (totalQuantity >= 3) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Add to Cart';
      } else if (totalQuantity > 0) {
        addToCartBtn.disabled = true;
        const remaining = 3 - totalQuantity;
        addToCartBtn.textContent = `Add ${remaining} more items`;
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Add to Cart';
      }
    }
  }
  
  updateProductPrices(totalQuantity) {
    // Determine which pricing tier we're in
    let discountedPrice = null;
    let hasDiscount = false;
    
    if (totalQuantity >= 5) {
      discountedPrice = '£9.24';
      hasDiscount = true;
    } else if (totalQuantity >= 4) {
      discountedPrice = '£9.57';
      hasDiscount = true;
    } else if (totalQuantity >= 3) {
      discountedPrice = '£9.97';
      hasDiscount = true;
    }
    
    // Update all product cards
    this.productCards.forEach(card => {
      const pricingContainer = card.querySelector('[data-product-pricing]');
      if (!pricingContainer) return;
      
      const regularPriceEl = pricingContainer.querySelector('[data-regular-price]');
      const discountedPriceEl = pricingContainer.querySelector('[data-discounted-price]');
      const priceHintEl = pricingContainer.querySelector('[data-price-hint]');
      
      if (hasDiscount) {
        // Show discounted price
        regularPriceEl.classList.add('has-discount');
        discountedPriceEl.textContent = discountedPrice;
        discountedPriceEl.style.display = 'inline';
        
        // Hide the hint when discount is active
        if (priceHintEl) {
          priceHintEl.classList.add('hidden');
        }
      } else {
        // Show regular price with hint
        regularPriceEl.classList.remove('has-discount');
        discountedPriceEl.style.display = 'none';
        
        // Show the hint when no discount
        if (priceHintEl) {
          priceHintEl.classList.remove('hidden');
        }
      }
    });
  }
}

// Add animations
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);

// Register the custom element
if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}